import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const onboardingCards = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_narcabusehelper/artifacts/k1rq8vdk_6.png",
    title: "Welcome",
    content: "You're not alone.\n\nThis app was created to help you make sense of confusing, painful, or overwhelming relational patterns—especially those shaped by narcissistic behavior.",
    color: 'var(--primary-blue)'
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_narcabusehelper/artifacts/hletkslc_2.png",
    title: "Why This Matters",
    content: "Not everything that hurts is \"your fault.\"\n\nHere, you'll learn to recognize unhealthy dynamics, strengthen boundaries, and reconnect with your own clarity and intuition.",
    color: 'var(--primary-green)'
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_narcabusehelper/artifacts/c53yl8wu_3.png",
    title: "Built by a Specialist",
    content: "Developed by a Licensed Marriage and Family Therapist specialized in narcissistic abuse and relational trauma, this app combines practical tools, grounded education, and step-by-step guidance to support your healing.",
    color: 'var(--accent-teal)'
  },
  {
    id: 4,
    image: "/guided-mode-illustration.png",
    title: "Guided Mode",
    content: "Guided Mode offers a step-by-step path through the material, similar to how a therapist might guide skill-building in session. It's not a substitute for therapy, but a supportive companion as you practice at your own pace.",
    color: '#10b981'
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_narcabusehelper/artifacts/1q6agb61_5.png",
    title: "Your Pace, Your Safety",
    content: "Move through the content as slowly or quickly as you want.\n\nEverything here is designed to help you build strength, insight, and a sense of safety without pressure.",
    color: '#ec4899'
  }
];

export default function OnboardingCarousel({ onComplete, isReview = false }) {
  const [currentCard, setCurrentCard] = useState(0);
  const { isDarkMode } = useTheme();

  const handleNext = () => {
    if (currentCard < onboardingCards.length - 1) {
      setCurrentCard(currentCard + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
    }
  };

  const card = onboardingCards[currentCard];

  // Image sizes: all cards use large images now (cards 4 and 5 removed, renumbered)
  const getImageSize = (id) => {
    if (id === 2 || id === 4) return { width: '450px', height: '450px' }; // Biggest
    if (id === 5) return { width: '420px', height: '420px' }; // Large
    return { width: '360px', height: '360px' }; // Standard for 1, 3
  };

  const imageSize = getImageSize(card.id);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: isDarkMode ? '#2d2a26' : '#E0D8CC' }}>
      {/* Close button for review mode */}
      {isReview && (
        <button
          onClick={onComplete}
          className="absolute top-4 right-4 z-50 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          style={{ color: isDarkMode ? '#e0e0e0' : 'var(--text-secondary)' }}
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      
      {/* Main content area - with extra bottom padding to avoid "Made with Emergent" overlap */}
      <div className="flex-1 flex flex-col items-center justify-center p-2 sm:p-4 pb-16 sm:pb-20">
        <div className="w-full max-w-2xl flex flex-col h-full" style={{ maxHeight: 'calc(100vh - 100px)' }}>
          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-2 sm:mb-3 flex-shrink-0">
            {onboardingCards.map((_, index) => (
              <div
                key={index}
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: index === currentCard ? '32px' : '8px',
                  background: index === currentCard ? card.color : 'rgba(255, 255, 255, 0.5)'
                }}
              />
            ))}
          </div>

          {/* Card - Responsive height - ALWAYS light background */}
          <Card 
            className="relative flex-1 flex flex-col overflow-hidden"
            style={{ 
              background: '#EDFBFF',
              border: `2px solid ${card.color}`,
              minHeight: '0'
            }}
          >
            <CardContent className="p-3 sm:p-5 md:p-6 text-center flex-1 flex flex-col overflow-hidden">
              {/* Image area - large on all screens */}
              <div className="flex justify-center items-center flex-shrink-0" style={{ height: '35vh', maxHeight: '288px' }}>
                <img 
                  src={card.image} 
                  alt={card.title}
                  className="object-contain max-h-72 sm:max-h-full"
                  style={imageSize}
                />
              </div>

              {/* Spacer to push text to bottom */}
              <div className="flex-1" />

              {/* Title and Content at bottom - dark mode adds soft gradient ONLY behind text */}
              <div 
                className="space-y-1 sm:space-y-2 flex-shrink-0 pb-2 sm:pb-8 md:pb-10 rounded-lg relative"
                style={isDarkMode ? {
                  background: 'linear-gradient(to top, rgba(40,40,50,0.85) 0%, rgba(40,40,50,0.6) 70%, transparent 100%)',
                  padding: '1.5rem 1rem 1rem 1rem',
                  margin: '0 -0.5rem -0.5rem -0.5rem',
                  borderRadius: '0.75rem'
                } : {}}
              >
                {/* Title - viewport responsive */}
                <h2 
                  className="font-bold sm:text-4xl md:text-5xl"
                  style={{ 
                    color: isDarkMode ? '#ffffff' : 'var(--text-primary)',
                    fontSize: 'clamp(1.25rem, 4.5vh, 3.5rem)',
                    textShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.5)' : 'none'
                  }}
                >
                  {card.title}
                </h2>

                {/* Content - 50% bigger text */}
                <div className="max-w-2xl mx-auto">
                  {card.content.split('\n\n').map((paragraph, idx) => (
                    <p 
                      key={idx}
                      className="leading-snug sm:leading-relaxed mb-1 sm:mb-2 last:mb-0 sm:text-2xl md:text-3xl"
                      style={{ 
                        color: isDarkMode ? '#f0f0f0' : 'var(--text-secondary)',
                        fontSize: 'clamp(0.85rem, 2.5vh, 2.25rem)',
                        textShadow: isDarkMode ? '0 1px 4px rgba(0,0,0,0.4)' : 'none'
                      }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation - Always visible below card */}
          <div className="flex items-center justify-between gap-3 pt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentCard === 0}
              className="px-4 py-2 text-sm font-medium"
              style={{
                borderColor: card.color,
                color: currentCard === 0 ? (isDarkMode ? '#888' : 'var(--text-secondary)') : card.color,
                opacity: currentCard === 0 ? 0.5 : 1,
                background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'transparent'
              }}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <span className="text-sm font-medium" style={{ color: isDarkMode ? '#ccc' : 'var(--text-secondary)' }}>
              {currentCard + 1} / {onboardingCards.length}
            </span>

            <Button
              onClick={handleNext}
              className="px-4 py-2 text-sm font-medium"
              style={{ 
                background: currentCard === onboardingCards.length - 1 
                  ? 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)'
                  : card.color,
                color: 'white'
              }}
            >
              {currentCard === onboardingCards.length - 1 ? 'Get Started' : 'Next'}
              {currentCard < onboardingCards.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Skip Button - Always visible */}
          {currentCard < onboardingCards.length - 1 && (
            <div className="text-center pt-3 flex-shrink-0">
              <Button
                variant="ghost"
                onClick={onComplete}
                className="text-sm font-medium px-6 py-2"
                style={{ 
                  color: isDarkMode ? '#5eead4' : 'var(--accent-teal)',
                  textDecoration: 'underline'
                }}
              >
                Skip Introduction
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
